import React, { useEffect, useState } from 'react';
import { ProductFruits } from 'react-product-fruits';
import { useAppDispatch } from '../../store/hooks';
import { getUserProfile } from '../../store/features/auth';
import { getValue } from '../../utility/helpers';

const ProductFruitsWrapper: React.FC = () => {
    const dispatch = useAppDispatch();
    const [userInfo, setUserInfo] = useState<any>(null);
    const hasFetchedRef = React.useRef(false);

    useEffect(() => {
        // Only fetch once per app session to avoid duplicate API calls
        if (hasFetchedRef.current) {
            return;
        }

        const fetchUserProfile = async () => {
            try {
                const companyId = getValue('companyId');
                if (!companyId) {
                    return;
                }

                // Mark as fetched to prevent duplicate calls
                hasFetchedRef.current = true;

                // Wait a bit to let DashboardPage or other pages fetch first
                // This allows us to use their API response instead of making our own call
                await new Promise(resolve => setTimeout(resolve, 500));

                // Check if profile data is already cached by another component
                const cachedUserId = getValue('userId');
                const cachedTierData = getValue('tierData');
                
                // If we have cached data, use it instead of making another API call
                if (cachedUserId && cachedTierData) {
                    try {
                        const tierData = JSON.parse(cachedTierData);
                        const userEmail = getValue('userEmail') || 'unknown_user';
                        const userName = getValue('userName') || 'User';
                        const companyName = getValue('companyName') || '';
                        
                        const user = {
                            username: userEmail,
                            email: userEmail,
                            firstname: userName.split(' ')[0] || 'User',
                            lastname: userName.split(' ')[1] || '',
                            props: {
                                userId: cachedUserId,
                                companyId: companyName,
                                plan: tierData?.name || 'Free',
                                industry: '',
                                website: '',
                                phone_number: ''
                            }
                        };
                        setUserInfo(user);
                        return; // Exit early, don't make API call
                    } catch (error) {
                        console.info('ProductFruitsWrapper - Error parsing cached data, will fetch fresh:', error);
                    }
                }

                // Only fetch if no cached data available
                const payload = { companyId, loader: false };
                const res = await dispatch(getUserProfile(payload)).unwrap();
                const data = res?.data;

                if (data) {
                    // User details are likely nested in a 'user' object based on other app usage
                    const user = {
                        username: data.user.email || 'unknown_user',
                        email: data.user.email,
                        firstname: data.user.full_name.split(' ')[0],
                        lastname: data.user.full_name.split(' ')[1],
                        props: {
                            userId: data.id,
                            companyId: data.company_name,
                            plan: data.tier_assigned.name,
                            industry: data.industry_type.name,
                            website: data.website_link,
                            phone_number: data.user.phone_number
                        }
                    };
                    setUserInfo(user);
                }
            } catch (error) {
                console.error('ProductFruitsWrapper - Error fetching user profile:', error);
                // Reset flag on error so it can retry
                hasFetchedRef.current = false;
            }
        };

        fetchUserProfile();
        // Empty dependency array - only run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!userInfo || !userInfo.username) {
        return null;
    }

    return (
        <ProductFruits
            workspaceCode="qqiDQBNs8e3o7P4T"
            language="en"
            user={userInfo}
        />
    );
};

export default ProductFruitsWrapper;
